/**
 * NotificationCenter Component
 * Real-time notification center with bell icon and dropdown
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/lib/axiosInstance';
import {
  Bell,
  Check,
  CheckCircle,
  Clock,
  HardDrives as Server,
  Info,
  Shield,
  ShieldWarning,
  Trash as Trash2,
  Warning as AlertTriangle,
  X
} from '@phosphor-icons/react';
import { getBackendUrl } from '@/lib/api';
import { parseApiError } from '@/lib/utils';

const BACKEND_URL = getBackendUrl();
// apiClient ya tiene baseURL=${BACKEND_URL}/api — paths van relativos.
// Concatenar otro /api aquí duplica el prefix con dominios relativos
// (ej. REACT_APP_BACKEND_URL='' produce /api/api/...).

// Notification type configurations — Onyx calibrated severities
const NOTIFICATION_TYPES = {
  vulnerability: {
    icon: Shield,
    color: 'text-red-400',
    bg: 'bg-red-500/[0.06]',
    border: 'border-red-500/[0.2]',
    chip: 'bg-red-500/[0.08] border-red-500/[0.2] text-red-300'
  },
  agent_online: {
    icon: Server,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/[0.06]',
    border: 'border-emerald-500/[0.2]',
    chip: 'bg-emerald-500/[0.08] border-emerald-500/[0.2] text-emerald-300'
  },
  agent_offline: {
    icon: Server,
    color: 'text-orange-400',
    bg: 'bg-orange-500/[0.06]',
    border: 'border-orange-500/[0.2]',
    chip: 'bg-orange-500/[0.08] border-orange-500/[0.2] text-orange-300'
  },
  scan_complete: {
    icon: CheckCircle,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/[0.06]',
    border: 'border-cyan-500/[0.2]',
    chip: 'bg-cyan-500/[0.08] border-cyan-500/[0.2] text-cyan-300'
  },
  // Rutas nuevas hacia Domain Admin (identity-diff). Color por severity:
  // high -> rojo (variante por defecto aquí), medium -> ámbar (ATTACK_PATH_MEDIUM).
  attack_path: {
    icon: ShieldWarning,
    color: 'text-red-400',
    bg: 'bg-red-500/[0.06]',
    border: 'border-red-500/[0.2]',
    chip: 'bg-red-500/[0.08] border-red-500/[0.2] text-red-300'
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-yellow-400',
    bg: 'bg-yellow-500/[0.06]',
    border: 'border-yellow-500/[0.2]',
    chip: 'bg-yellow-500/[0.08] border-yellow-500/[0.2] text-yellow-300'
  },
  info: {
    icon: Info,
    color: 'text-blue-400',
    bg: 'bg-blue-500/[0.06]',
    border: 'border-blue-500/[0.2]',
    chip: 'bg-blue-500/[0.08] border-blue-500/[0.2] text-blue-300'
  }
};

// Variante ámbar para rutas a Domain Admin de severidad media (metadata.severity === 'medium').
const ATTACK_PATH_MEDIUM = {
  icon: ShieldWarning,
  color: 'text-amber-400',
  bg: 'bg-amber-500/[0.06]',
  border: 'border-amber-500/[0.2]',
  chip: 'bg-amber-500/[0.08] border-amber-500/[0.2] text-amber-300'
};

// Resuelve el config visual de una notificación, aplicando color por severity
// en las alertas de rutas nuevas a Domain Admin (tipo attack_path).
const resolveConfig = (notification) => {
  if (notification.type === 'attack_path' && notification.metadata?.severity === 'medium') {
    return ATTACK_PATH_MEDIUM;
  }
  return NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.info;
};

const NotificationItem = ({ notification, onDismiss, onMarkRead, onNavigate }) => {
  const config = resolveConfig(notification);
  const Icon = config.icon;

  const handleActivate = () => {
    onMarkRead(notification.id);
    if (notification.type === 'attack_path') {
      onNavigate?.('/alerts');
    }
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'ahora';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Notificación: ${notification.title}`}
      className={`
        relative p-3 rounded-md border transition-all cursor-pointer
        ${notification.read ? 'opacity-60' : ''}
        ${config.bg} ${config.border}
        hover:opacity-100 hover:bg-white/[0.02]
      `}
      onClick={handleActivate}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleActivate(); } }}
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-8 h-8 rounded-md flex items-center justify-center ${config.bg} border ${config.border}`}>
          <Icon className={`h-4 w-4 ${config.color}`} weight="regular" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-slate-100 tracking-tight truncate pr-4">
            {notification.title}
          </p>
          <p className="text-[12px] text-slate-400 mt-1 line-clamp-2 leading-relaxed">
            {notification.message}
          </p>
          <div className="flex items-center gap-1.5 mt-2">
            <Clock className="h-3 w-3 text-slate-500" weight="regular" aria-hidden="true" />
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
              {timeAgo(notification.timestamp)}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(notification.id);
          }}
          aria-label="Descartar notificación"
          className="shrink-0 p-1 text-slate-500 hover:text-slate-200 hover:bg-v4-panel/[0.06] rounded-md transition-colors"
        >
          <X className="h-3 w-3" weight="regular" aria-hidden="true" />
        </button>
      </div>
      {!notification.read && (
        <span
          aria-hidden="true"
          className="absolute top-2.5 right-9 w-1.5 h-1.5 bg-cyan-400 rounded-full"
        />
      )}
    </div>
  );
};

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [feedError, setFeedError] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const handleNotificationNavigate = useCallback((path) => {
    setIsOpen(false);
    navigate(path);
  }, [navigate]);

  // Fetch notifications from backend activity feed
  const checkForNewNotifications = useCallback(async () => {
    try {
      // Try to get from backend activity feed first
      const activityRes = await apiClient.get('/activity/feed?limit=20').catch(() => null);

      if (activityRes && activityRes.data) {
        const stored = JSON.parse(localStorage.getItem('notifications') || '[]');
        const readIds = new Set(stored.filter(n => n.read).map(n => n.id));

        // Merge new activities with stored read status
        const newNotifications = activityRes.data.map(activity => ({
          ...activity,
          read: readIds.has(activity.id)
        }));

        setNotifications(newNotifications);
        localStorage.setItem('notifications', JSON.stringify(newNotifications));
        setFeedError(null);
        return;
      }

      // Fallback to client-side generation if backend endpoint not available
      let failedSources = 0;
      const orFallback = (request, fallback) => request.catch(() => { failedSources += 1; return fallback; });
      const [statsRes, agentsRes, scansRes] = await Promise.all([
        orFallback(apiClient.get('/dashboard/stats'), { data: {} }),
        orFallback(apiClient.get('/agents'), { data: [] }),
        orFallback(apiClient.get('/scans?limit=5'), { data: [] })
      ]);
      // Si no respondió nada, "Sin notificaciones · Todo al día" sería mentira.
      setFeedError(failedSources === 3 ? 'No se pudieron comprobar las notificaciones: el servidor no responde' : null);

      const newNotifications = [];
      const now = new Date();
      const stored = JSON.parse(localStorage.getItem('notifications') || '[]');
      const storedIds = new Set(stored.map(n => n.id));

      // Check for critical vulnerabilities
      const criticalVulns = statsRes.data?.critical_vulnerabilities || 0;
      if (criticalVulns > 0) {
        const vulnId = `vuln-critical-${criticalVulns}`;
        if (!storedIds.has(vulnId)) {
          newNotifications.push({
            id: vulnId,
            type: 'vulnerability',
            title: 'Vulnerabilidades Críticas',
            message: `Se han detectado ${criticalVulns} vulnerabilidades críticas que requieren atención inmediata.`,
            timestamp: now.toISOString(),
            read: false
          });
        }
      }

      // Check for offline agents
      const agents = agentsRes.data || [];
      const offlineAgents = agents.filter(a => a.status !== 'active');
      if (offlineAgents.length > 0) {
        const offlineId = `agents-offline-${offlineAgents.length}`;
        if (!storedIds.has(offlineId)) {
          newNotifications.push({
            id: offlineId,
            type: 'agent_offline',
            title: 'Agentes Desconectados',
            message: `${offlineAgents.length} agente(s) no están reportando: ${offlineAgents.map(a => a.hostname).slice(0, 3).join(', ')}${offlineAgents.length > 3 ? '...' : ''}`,
            timestamp: now.toISOString(),
            read: false
          });
        }
      }

      // Check for recent scans
      const scans = scansRes.data || [];
      const recentScans = scans.filter(s => {
        const scanDate = new Date(s.timestamp || s.created_at);
        return (now - scanDate) < 3600000; // Last hour
      });

      if (recentScans.length > 0 && !storedIds.has('recent-scans')) {
        newNotifications.push({
          id: `recent-scans-${now.getTime()}`,
          type: 'scan_complete',
          title: 'Escaneos Recientes',
          message: `${recentScans.length} escaneo(s) completado(s) en la última hora.`,
          timestamp: now.toISOString(),
          read: false
        });
      }

      // Merge with existing notifications
      const merged = [...newNotifications, ...stored].slice(0, 50);
      setNotifications(merged);
      localStorage.setItem('notifications', JSON.stringify(merged));

    } catch (error) {
      setFeedError(parseApiError(error.response?.data?.detail, 'No se pudieron comprobar las notificaciones'));
    }
  }, []);

  // Load notifications on mount
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('notifications') || '[]');
    setNotifications(stored);

    // Initial check
    checkForNewNotifications();

    // Check every 30 seconds
    const interval = setInterval(checkForNewNotifications, 30000);
    return () => clearInterval(interval);
  }, [checkForNewNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  const dismissNotification = (id) => {
    const updated = notifications.filter(n => n.id !== id);
    setNotifications(updated);
    localStorage.setItem('notifications', JSON.stringify(updated));
  };

  const markAsRead = (id) => {
    const updated = notifications.map(n =>
      n.id === id ? { ...n, read: true } : n
    );
    setNotifications(updated);
    localStorage.setItem('notifications', JSON.stringify(updated));
  };

  const markAllAsRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updated);
    localStorage.setItem('notifications', JSON.stringify(updated));
  };

  const clearAll = () => {
    setNotifications([]);
    localStorage.setItem('notifications', JSON.stringify([]));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`Notificaciones${unreadCount > 0 ? ` (${unreadCount} sin leer)` : ''}`}
        aria-expanded={isOpen}
        className={`
          relative w-9 h-9 inline-flex items-center justify-center rounded-md transition-colors
          border border-transparent
          ${isOpen
            ? 'bg-cyan-500/[0.1] text-cyan-400 border-cyan-500/[0.2]'
            : 'text-slate-300 hover:text-cyan-400 hover:bg-white/[0.06] hover:border-white/[0.08]'
          }
        `}
      >
        <Bell className="h-4 w-4" weight={isOpen ? 'fill' : 'regular'} aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-500 text-v4-on-accent text-[10px] font-mono font-semibold rounded-full flex items-center justify-center border border-v4-bg"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          role="dialog"
          aria-label="Panel de notificaciones"
          className="absolute right-0 mt-2 w-96 bg-slate-900/95 backdrop-blur-xl border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden z-50"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400/90">
                // NOTIFICATIONS
              </div>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-cyan-500/[0.1] text-cyan-300 border border-cyan-500/[0.2] rounded">
                  {unreadCount} nuevas
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllAsRead}
                  aria-label="Marcar todo como leído"
                  className="p-1.5 text-slate-500 hover:text-cyan-400 rounded-md hover:bg-v4-panel/[0.06] transition-colors"
                  title="Marcar todo como leído"
                >
                  <Check className="h-3.5 w-3.5" weight="regular" aria-hidden="true" />
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  aria-label="Limpiar todas las notificaciones"
                  className="p-1.5 text-slate-500 hover:text-red-400 rounded-md hover:bg-red-500/[0.08] transition-colors"
                  title="Limpiar todo"
                >
                  <Trash2 className="h-3.5 w-3.5" weight="regular" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto p-2 space-y-2">
            {feedError && (
              <p role="alert" data-testid="notifications-load-error" className="px-3 py-2 rounded-md border border-red-500/[0.25] bg-red-500/[0.06] text-[12px] text-red-300">
                {feedError}
              </p>
            )}
            {notifications.length === 0 && feedError ? null : notifications.length === 0 ? (
              <div className="text-center py-10">
                <Bell className="h-10 w-10 text-slate-700 mx-auto mb-3" weight="regular" aria-hidden="true" />
                <p className="text-[13px] text-slate-400">Sin notificaciones</p>
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-600 mt-1">Todo al día</p>
              </div>
            ) : (
              notifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onDismiss={dismissNotification}
                  onMarkRead={markAsRead}
                  onNavigate={handleNotificationNavigate}
                />
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t border-white/[0.06] bg-slate-950/40">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="w-full text-center text-[11px] font-mono uppercase tracking-wider text-cyan-400/90 hover:text-cyan-300 transition-colors"
              >
                Ver todas
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
